import { useQuery } from '@tanstack/react-query';
import * as z from 'zod';

import {
  WorkflowConfigurationMap,
  WorkflowDataType,
  WorkflowScopeType,
  WorkflowStatus,
  WorkflowTriggerType,
} from '@invenco/common-domain/enums';
import { WorkflowDTO } from '@invenco/common-interface/automation';

import { SelectItem } from '@/components/core/select';
import { SelectFormField } from '@/components/forms/select-form-field';
import { SwitchFormField } from '@/components/forms/switch-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { TextareaFormField } from '@/components/forms/textarea-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { workflowDataTypeTitle, workflowTriggerTypeTitle } from '@/shared/title-maps';

export const workflowFormSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name'),
  description: z.string().trim(),
  dataType: z.enum(WorkflowDataType),
  triggerType: z.enum(WorkflowTriggerType),
  // friendly form of the `status` enum; converted by the helpers below
  active: z.boolean(),
  // Only meaningful for WMS_INSTANCE scope, where it pins the workflow to an
  // instance; account-scoped workflows leave it empty (the API derives scope).
  scopeId: z.string().optional(),
});

export type WorkflowFormValues = z.infer<typeof workflowFormSchema>;

/** The workflow fields the metadata form reads from. */
type WorkflowFormSource = Pick<
  WorkflowDTO,
  'name' | 'description' | 'dataType' | 'triggerType' | 'status'
> & {
  scopeId?: string;
};

/** Seeds the form from a persisted workflow, mapping `status` → `active`. */
export function workflowToFormValues(workflow: WorkflowFormSource): WorkflowFormValues {
  return {
    name: workflow.name,
    description: workflow.description ?? '',
    dataType: workflow.dataType,
    triggerType: workflow.triggerType,
    active: workflow.status === WorkflowStatus.ACTIVE,
    scopeId: workflow.scopeId ?? '',
  };
}

/** Maps validated form values back to a workflow patch, `active` → `status`. */
export function formValuesToWorkflowPatch(values: WorkflowFormValues): Partial<WorkflowDTO> {
  return {
    name: values.name,
    description: values.description,
    dataType: values.dataType,
    triggerType: values.triggerType,
    status: values.active ? WorkflowStatus.ACTIVE : WorkflowStatus.INACTIVE,
  };
}

type Props = {
  scopeType: WorkflowScopeType;
  /**
   * Data type and trigger are immutable post-creation: editable when new,
   * disabled otherwise.
   */
  isNew?: boolean;
};

export function WorkflowFormFields({ scopeType, isNew }: Props) {
  const { dataTypes, triggerTypes } = WorkflowConfigurationMap[scopeType];
  const isWmsScope = scopeType === WorkflowScopeType.WMS_INSTANCE;

  const { warehousingGateway } = useGateways();
  const { data: wmsInstances } = useQuery({
    queryKey: ['wmsInstances'],
    queryFn: ({ signal }) => warehousingGateway.getWmsInstances(undefined, { signal }),
    enabled: isWmsScope,
  });

  return (
    <div className="space-y-4">
      <TextFormField name="name" label="Name" />
      <SelectFormField name="dataType" label="Data type" disabled={!isNew}>
        {dataTypes.map((dataType) => (
          <SelectItem key={dataType} value={dataType}>
            {workflowDataTypeTitle[dataType]}
          </SelectItem>
        ))}
      </SelectFormField>
      <SelectFormField name="triggerType" label="Trigger" disabled={!isNew}>
        {triggerTypes.map((triggerType) => (
          <SelectItem key={triggerType} value={triggerType}>
            {workflowTriggerTypeTitle[triggerType]}
          </SelectItem>
        ))}
      </SelectFormField>
      {isWmsScope && (
        <SelectFormField
          name="scopeId"
          label="WMS instance"
          placeholder="Select a WMS instance"
          disabled={!isNew}
        >
          {(wmsInstances ?? []).map((instance) => (
            <SelectItem key={instance.id} value={instance.id}>
              {instance.code}
            </SelectItem>
          ))}
        </SelectFormField>
      )}
      <TextareaFormField name="description" label="Description" rows={3} />
      <SwitchFormField name="active" label="Active" />
    </div>
  );
}
